import { useRouteError, isRouteErrorResponse, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { AlertTriangleIcon, RefreshCwIcon, HomeIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'

export function RouteErrorBoundary() {
  const error = useRouteError()
  const navigate = useNavigate()
  const { t } = useTranslation('layout')

  let title = t('error.unexpected_title')
  let message = t('error.unexpected_message')
  let detail: string | null = null

  if (isRouteErrorResponse(error)) {
    if (error.status === 404) {
      title = t('error.not_found_title')
      message = t('error.not_found_message')
    } else {
      title = t('error.status_title', { status: error.status })
      message = error.statusText || message
    }
  } else if (error instanceof Error) {
    message = error.message
    if (
      error.message.includes('does not provide an export') ||
      error.message.includes('Failed to fetch dynamically imported module')
    ) {
      title = t('error.loading_title')
      message = t('error.loading_message')
      detail = error.message
    }
  }

  return (
    <div className="flex min-h-[50vh] flex-col items-center justify-center gap-6 px-4 text-center">
      <div className="flex size-16 items-center justify-center rounded-full bg-destructive/10">
        <AlertTriangleIcon className="size-8 text-destructive" />
      </div>

      <div className="max-w-md space-y-2">
        <h1 className="text-2xl font-bold tracking-tight">{title}</h1>
        <p className="text-muted-foreground">{message}</p>
        {detail && (
          <pre className="mt-3 max-w-full overflow-x-auto rounded-lg bg-muted/50 p-3 text-left text-xs">
            {detail}
          </pre>
        )}
      </div>

      <div className="flex items-center gap-3">
        <Button variant="outline" onClick={() => window.location.reload()} className="gap-2">
          <RefreshCwIcon className="size-4" />
          {t('error.reload_page')}
        </Button>
        <Button onClick={() => navigate('/dashboard', { replace: true })} className="gap-2">
          <HomeIcon className="size-4" />
          {t('error.go_home')}
        </Button>
      </div>
    </div>
  )
}
